import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import {
  useTimebankBalance,
  useTimebankTransactions,
  useCancelTimebankRequest,
  type TimebankTransaction,
  type TimebankStatus,
} from "@/hooks/use-timebank";
import { useHighlight } from "@/hooks/use-highlight";
import { useSearchParams } from "react-router-dom";
import { formatDate, formatHours } from "@/lib/format";
import { TIMEBANK_STATUS_LABEL, TIMEBANK_STATUS_STYLE } from "@/lib/timebank-status";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";
import RequestWithdrawalDialog from "./RequestWithdrawalDialog";

export default function TimebankCard() {
  const { activeUser } = useImpersonation();
  const userId = activeUser?.id ?? null;
  const { data: balance } = useTimebankBalance(userId);
  const { data: transactions = [] } = useTimebankTransactions(userId);
  const cancelMut = useCancelTimebankRequest();

  const [withdrawalOpen, setWithdrawalOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const highlightTarget = searchParams.get("highlight");

  // Auto-åpne historikk når vi kommer hit via et timebank-varsel,
  // slik at deep-linket transaksjon faktisk er synlig før scroll.
  useEffect(() => {
    if (!highlightTarget) return;
    if (transactions.some((t) => t.id === highlightTarget)) {
      setHistoryOpen(true);
    }
  }, [highlightTarget, transactions]);

  const saldo = balance?.balance ?? 0;
  const pendingDeposit = balance?.pendingDeposit ?? 0;
  const pendingWithdrawal = balance?.pendingWithdrawal ?? 0;
  const hasPending = pendingDeposit > 0 || pendingWithdrawal > 0;

  if (!userId) return null;

  const recent = transactions.slice(0, 8);

  return (
    <div className="border-[1.5px] border-foreground bg-card">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4">
        <button
          onClick={() => setHistoryOpen(!historyOpen)}
          className="flex items-center gap-3 flex-1 min-w-0 text-left flex-wrap"
        >
          <SrIcon name="clock" className="w-5 h-5 text-primary shrink-0" />
          <div className="min-w-0">
            <h3 className="font-display text-lg leading-tight">Timebank</h3>
            <p className="text-[11px] text-muted-foreground">Avspassering og oppsparte timer</p>
          </div>
          <div className="ml-auto md:ml-6 flex items-baseline gap-1 shrink-0">
            <span className="font-display text-2xl tabular-nums leading-none">
              {formatHours(saldo)}
            </span>
            <span className="text-[11px] text-muted-foreground">saldo</span>
          </div>
          {hasPending && (
            <span className="md:ml-3 text-[11px] px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium">
              {pendingDeposit > 0 && `+${formatHours(pendingDeposit)} venter`}
              {pendingDeposit > 0 && pendingWithdrawal > 0 && " · "}
              {pendingWithdrawal > 0 && `−${formatHours(pendingWithdrawal)} venter`}
            </span>
          )}
        </button>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            variant="outline"
            className="flex-1 md:flex-none min-h-[44px] md:min-h-0 md:h-7 text-xs"
            onClick={() => setWithdrawalOpen(true)}
            disabled={saldo <= 0 && pendingWithdrawal === 0 && saldo === 0}
          >
            <SrIcon name="calendar" className="w-3 h-3 mr-1" />
            Søk avspassering
          </Button>
          <button
            onClick={() => setHistoryOpen(!historyOpen)}
            className="min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 md:p-1 flex items-center justify-center"
            aria-label={historyOpen ? "Skjul historikk" : "Vis historikk"}
          >
            <SrIcon
              name={historyOpen ? "chevron-up" : "chevron-down"}
              className="w-4 h-4 text-muted-foreground"
            />
          </button>
        </div>
      </div>

      {historyOpen && (
        <div className="border-t">
          {recent.length === 0 ? (
            <p className="text-xs text-muted-foreground px-4 py-6 text-center">
              Ingen søknader registrert ennå.
            </p>
          ) : (
            <ul className="divide-y divide-border/50">
              {recent.map((t) => (
                <TimebankHistoryRow
                  key={t.id}
                  t={t}
                  userId={userId}
                  onCancel={() => cancelMut.mutate({ id: t.id, user_id: userId })}
                />
              ))}
            </ul>
          )}
        </div>
      )}

      <RequestWithdrawalDialog
        open={withdrawalOpen}
        onOpenChange={setWithdrawalOpen}
        userId={userId}
        currentBalance={saldo}
      />
    </div>
  );
}

function TimebankHistoryRow({
  t,
  userId: _userId,
  onCancel,
}: {
  t: TimebankTransaction;
  userId: string;
  onCancel: () => void;
}) {
  const ref = useHighlight<HTMLLIElement>(t.id);
  const isDeposit = t.kind === "deposit";
  const taskLabel = t.assignment?.budget_lines?.description ?? "oppgave";
  const projectName = t.assignment?.budget_lines?.projects?.name ?? null;
  return (
    <li ref={ref} className="flex items-center gap-3 px-4 py-2 text-sm">
      <SrIcon
        name={isDeposit ? "arrow-down" : "arrow-up"}
        className={cn(
          "w-3.5 h-3.5 shrink-0",
          isDeposit ? "text-success" : "text-primary",
        )}
      />
      <div className="flex-1 min-w-0">
        <p className="truncate">
          {isDeposit
            ? projectName
              ? `${taskLabel} (${projectName})`
              : taskLabel
            : t.start_date && t.end_date
              ? `${formatDate(t.start_date)} – ${formatDate(t.end_date)}`
              : "Avspassering"}
        </p>
        {t.comment && (
          <p className="text-[11px] text-muted-foreground truncate">{t.comment}</p>
        )}
        {t.status === "rejected" && t.rejected_reason && (
          <p className="text-[11px] text-destructive truncate">{t.rejected_reason}</p>
        )}
      </div>
      <span className="text-sm tabular-nums shrink-0">
        {isDeposit ? "+" : "−"}
        {formatHours(Number(t.hours))}
      </span>
      <StatusPill status={t.status} />
      {t.status === "pending" && (
        <button
          onClick={onCancel}
          type="button"
          title="Trekk søknad"
          aria-label="Trekk søknad"
          className="p-1 text-muted-foreground hover:text-destructive"
        >
          <SrIcon name="x" className="w-3.5 h-3.5" />
        </button>
      )}
    </li>
  );
}

function StatusPill({ status }: { status: TimebankStatus }) {
  // VIB-2766: etiketten og stilen bor i `@/lib/timebank-status`, delt med
  // admin-tabellen og Timebank-fanen på ansattprofilen. Lå tidligere inline
  // her, og hadde alt drevet fra admin-tabellens variant.
  return (
    <span
      className={cn(
        "text-[10px] px-1.5 py-0.5 rounded shrink-0",
        TIMEBANK_STATUS_STYLE[status],
      )}
    >
      {TIMEBANK_STATUS_LABEL[status]}
    </span>
  );
}
